import { apiFetch } from './api';

export const TICKET_STATUSES = ['open', 'pending', 'resolved', 'closed'] as const;
export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export interface TicketRow {
  id: string;
  number: number;
  siteId: string;
  siteName: string;
  sessionId: string | null;
  visitorId: string | null;
  visitorName: string | null;
  visitorEmail: string | null;
  subject: string;
  description: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  assigneeId: string | null;
  assigneeName: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  closedAt: string | null;
}

export interface TicketEventRow {
  id: string;
  type: 'created' | 'status' | 'priority' | 'assignee' | 'subject' | 'comment';
  fromValue: string | null;
  toValue: string | null;
  body: string | null;
  createdAt: string;
  actorId: string | null;
  actorName: string | null;
}

export interface TicketDetail {
  ticket: TicketRow;
  events: TicketEventRow[];
  /** Names for agent ids that appear in assignee events. */
  agentNames: Record<string, string>;
}

export interface TicketListFilters {
  siteId?: string;
  sessionId?: string;
  status?: TicketStatus | 'active' | 'all';
  assignee?: string;
  priority?: TicketPriority;
  q?: string;
  page?: number;
}

export function listTickets(f: TicketListFilters) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') params.set(k, String(v));
  return apiFetch<{ items: TicketRow[]; total: number; page: number; limit: number }>(`/api/tickets?${params}`);
}

export const getTicket = (id: string) => apiFetch<TicketDetail>(`/api/tickets/${id}`);

export interface CreateTicketInput {
  siteId?: string;
  sessionId?: string;
  subject?: string;
  description?: string;
  priority?: TicketPriority;
  /** Omit to route automatically; null to leave unassigned. */
  assigneeId?: string | null;
}

export const createTicket = (input: CreateTicketInput) =>
  apiFetch<{ ticket: TicketRow }>('/api/tickets', { method: 'POST', body: JSON.stringify(input) });

export const updateTicket = (
  id: string,
  patch: Partial<{ subject: string; status: TicketStatus; priority: TicketPriority; assigneeId: string | null }>,
) => apiFetch<{ ticket: TicketRow }>(`/api/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const addTicketComment = (id: string, body: string) =>
  apiFetch<{ ok: boolean }>(`/api/tickets/${id}/comments`, { method: 'POST', body: JSON.stringify({ body }) });

/** Agents assigned to a site — the assignee dropdown. */
export const listSiteAgents = (siteId: string) =>
  apiFetch<{ siteAgents: Array<{ agentId: string; displayName: string; disabledAt: string | null }> }>(
    `/api/admin/sites/${siteId}/agents`,
  ).then((r) => r.siteAgents.filter((a) => !a.disabledAt));
